// Pricing tiers and comparison rows. Pulled out of page.tsx so both the
// (server) page and any (client) island can read the same source of truth.
// Mirrors backend/app/core/plans.py PLAN_CONFIG (canonical 5-tier: starter, pro, business, scale, admin).

export type Cell = string | boolean

export const PLANS = [
  {
    name: 'Starter',
    slug: 'starter',
    monthly: 199,
    annual: 166,
    annualTotal: 1990,
    blurb: 'For the operator running competitive intel solo.',
    features: [
      '20 tracked companies',
      'Daily digest + same-day critical alerts',
      'All signal modules',
      '6-month signal history',
      'Side-by-side compare (3 / day)',
      'CSV export',
      'Direct email support',
    ],
    cta: { label: 'Start 14-day trial', href: '/signup?plan=starter' },
    featured: false,
  },
  {
    name: 'Pro',
    slug: 'pro',
    monthly: 549,
    annual: 458,
    annualTotal: 5490,
    blurb: 'For teams losing deals to information asymmetry.',
    features: [
      '60 tracked companies',
      'Same-day Slack + Microsoft Teams + Telegram + email',
      '12-month signal history',
      'Compare up to 10 / day',
      'CSV + JSON export',
      'API access (10K req/day)',
      '10 due-diligence reports / mo',
      '10 auto-battlecards / mo',
      'Custom alert rules + webhooks',
      'Up to 5 team members',
      'Direct email support',
    ],
    cta: { label: 'Start 14-day trial', href: '/signup?plan=pro' },
    featured: false,
  },
  {
    name: 'Business',
    slug: 'business',
    monthly: 1299,
    annual: 1083,
    annualTotal: 12990,
    blurb: 'For teams that are done flying blind.',
    features: [
      '200 tracked companies',
      'Same-day Slack + Microsoft Teams + Telegram + email',
      '24-month signal history',
      'Side-by-side compare up to 50 / day',
      'CSV + JSON export + due-diligence + battlecard PDFs',
      'API access (50K req/day)',
      '50 due-diligence reports / mo',
      'High-volume auto-battlecards (fair use)',
      'Custom alert rules + webhooks',
      'Up to 15 team members',
      'Direct email support',
    ],
    cta: { label: 'Start 14-day trial', href: '/signup?plan=business' },
    featured: true,
  },
  {
    name: 'Scale',
    slug: 'scale',
    monthly: 2499,
    annual: 2083,
    annualTotal: 24990,
    blurb: 'For organizations operationalizing competitive intelligence.',
    features: [
      '500 tracked companies',
      'Same-day Slack + Microsoft Teams + Telegram + email',
      'Unlimited signal history',
      'Side-by-side compare up to 200 / day',
      'CSV + JSON export + due-diligence + battlecard PDFs',
      'API access (200K req/day)',
      'Unlimited due-diligence reports',
      'Unlimited auto-battlecards (fair use)',
      'Custom alert rules + webhooks',
      'Up to 50 team members',
      'Direct email support',
    ],
    cta: { label: 'Start 14-day trial', href: '/signup?plan=scale' },
    featured: false,
  },
] as const

export const COMPARE: {
  category: string
  rows: { name: string; values: [Cell, Cell, Cell, Cell] }[]
}[] = [
  {
    category: 'Watchlist',
    rows: [
      { name: 'Companies tracked', values: ['20', '60', '200', '500'] },
      { name: 'Signal history', values: ['6 months', '12 months', '24 months', 'Unlimited'] },
      { name: 'Refresh cadence', values: ['Daily', 'Daily', 'Daily', 'Daily'] },
      { name: 'All signal pipelines + public-registry feeds', values: [true, true, true, true] },
    ],
  },
  {
    category: 'Alerts & routing',
    rows: [
      { name: 'Daily email digest', values: [true, true, true, true] },
      { name: 'Same-day critical alerts', values: [true, true, true, true] },
      { name: 'Same-day Slack alerts', values: [false, true, true, true] },
      { name: 'Microsoft Teams dispatcher', values: [false, true, true, true] },
      { name: 'Telegram dispatcher', values: [false, true, true, true] },
      { name: 'Custom alert rules + webhooks', values: [false, true, true, true] },
    ],
  },
  {
    category: 'Analysis',
    rows: [
      { name: 'Side-by-side compare (per day)', values: ['3', '10', '50', '200'] },
      { name: 'Due-diligence reports', values: [false, '10 / mo', '50 / mo', 'Unlimited'] },
      { name: 'Auto-battlecards', values: [false, '10 / mo', 'High-volume (fair use)', 'Unlimited (fair use)'] },
    ],
  },
  {
    category: 'Export & API',
    rows: [
      { name: 'CSV export', values: [true, true, true, true] },
      { name: 'JSON export', values: [false, true, true, true] },
      { name: 'PDF export', values: [false, false, true, true] },
      { name: 'API access', values: [false, '10K req/day', '50K req/day', '200K req/day'] },
    ],
  },
  {
    category: 'Team & security',
    rows: [
      { name: 'Team members', values: ['1', '5', '15', '50'] },
      { name: 'Audit log', values: [false, false, false, true] },
    ],
  },
]

export const FAQ: { q: string; a: string }[] = [
  {
    q: 'Is there a free trial?',
    a: 'Every plan starts with a 14-day free trial. We collect a card at signup but do not charge it until day 15. Cancel any time before then and nothing is charged.',
  },
  {
    q: 'What happens if I cancel before day 15?',
    a: 'Nothing is charged. The trial runs for 14 full days. If you cancel before day 15, the first charge never happens and you owe us nothing. You can cancel from your billing settings in two clicks.',
  },
  {
    q: "What's your refund policy?",
    a: 'A full refund within 7 calendar days of your first charge, no questions asked, even if you have used the product. Email support@pulsesignal.co and we refund you in full. After day 7, the full refund policy at /refund-policy applies, and statutory rights like the EU and UK 14-day cooling-off period stay available on top. The trial gives you two weeks before any charge, and the refund window gives you another week after.',
  },
  {
    q: 'Monthly or annual billing?',
    a: 'Annual is the default and the better deal: you pay for 10 months and get 12, so two months are free on every plan. You can switch to monthly with the toggle at the top of this page. Either way the 14-day trial is the same.',
  },
  {
    q: 'Can I switch plans at any time?',
    a: 'Yes. Open the billing portal from Settings > Billing to change plans. Upgrade and Polar charges the prorated difference for the rest of the current cycle immediately. Downgrade and the change applies at your next billing date; you keep your current plan until then.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'Visa, Mastercard, and American Express via Polar. We can invoice annual plans on request.',
  },
  {
    q: 'How do team seats work?',
    a: 'Starter is 1 seat, Pro is 5, Business is 15, Scale is 50. Need more? Talk to us about Scale+ pricing.',
  },
  {
    q: 'Is there a discount for annual billing?',
    a: 'Yes. Annual billing is the equivalent of 10 months for 12 (2 months free) on every plan. It is the default on the pricing page; switch to monthly with the toggle if you prefer.',
  },
  {
    q: 'What does "Unlimited (fair use)" mean?',
    a: 'It applies to the auto-battlecards and due-diligence reports marked "unlimited" or "high-volume" (fair use), not to tracked companies (those caps are fixed per plan). Unlimited covers normal human use. Fair-use thresholds catch scripting and abuse, not real work. Above the threshold we throttle rather than block, and we reach out before you hit the wall. Exact thresholds are available on request: email support@pulsesignal.co.',
  },
]
